import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { User, ChevronRight } from 'lucide-react';

const LeaderCard = ({ leader, t, onClick }) => {
  const hasImage = leader.image_url && !leader.image_url.includes('unsplash') && leader.image_url.length > 5;
  const hasName = t(leader.name)?.trim();
  return (
    <Card
      className="overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer group"
      data-testid={`leader-card-${leader._id}`}
      onClick={onClick}
    >
      <div className="aspect-[3/4] overflow-hidden bg-gray-100 relative">
        {hasImage ? (
          <img src={leader.image_url} alt={hasName || t(leader.position)} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
            <User className="h-24 w-24 text-gray-400 mb-4" />
            <p className="text-gray-500 text-sm">{t({ en: 'To be announced', bn: 'ঘোষণা করা হবে' })}</p>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex items-center text-white text-sm">
            <span>{t({ en: 'View Profile', bn: 'প্রোফাইল দেখুন' })}</span>
            <ChevronRight className="h-4 w-4 ml-1" />
          </div>
        </div>
      </div>
      <CardContent className="p-6 text-center">
        {hasName && <h3 className="text-xl font-bold text-gray-900 mb-1">{hasName}</h3>}
        <p className="text-party-red font-semibold text-sm">{t(leader.position)}</p>
      </CardContent>
    </Card>
  );
};

const Leadership = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaders = async () => {
      try {
        const response = await fetch('/api/leaders/');
        if (response.ok) setLeaders(await response.json());
      } catch (error) {
        console.error('Error fetching leaders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaders();
  }, []);

  const nationalLeaders = leaders.filter(l => l.level === 'national');
  const districtLeaders = leaders.filter(l => l.level === 'district');
  const centralLeaders = leaders.filter(l => l.level === 'central');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="leadership-page">
      <section className="bg-party-red py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t({ en: 'Our Leadership', bn: 'আমাদের নেতৃত্ব' })}
          </h1>
          <p className="text-lg text-red-100 max-w-3xl mx-auto">
            {t({ en: 'Dedicated leaders with a vision for a progressive, inclusive Bangladesh.', bn: 'প্রগতিশীল, অন্তর্ভুক্তিমূলক বাংলাদেশের দৃষ্টিভঙ্গি সহ নিবেদিত নেতারা।' })}
          </p>
        </div>
      </section>

      {/* National Leadership */}
      {nationalLeaders.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {t({ en: 'National Leadership', bn: 'জাতীয় নেতৃত্ব' })}
              </h2>
              <div className="w-24 h-1 bg-party-red mx-auto rounded-full"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {nationalLeaders.map((leader) => (
                <LeaderCard
                  key={leader._id}
                  leader={leader}
                  t={t}
                  onClick={() => navigate(`/leadership/${leader._id}`)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Separator */}
      {nationalLeaders.length > 0 && districtLeaders.length > 0 && (
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-party-red/30 to-party-red/30"></div>
            <div className="flex items-center gap-2 px-4 py-2 bg-party-red/5 rounded-full border border-party-red/20">
              <div className="w-2 h-2 bg-party-red rounded-full"></div>
              <span className="text-sm font-medium text-party-red tracking-wide">
                {t({ en: 'DISTRICT LEADERSHIP', bn: 'জেলা নেতৃত্ব' })}
              </span>
              <div className="w-2 h-2 bg-party-red rounded-full"></div>
            </div>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-party-red/30 to-party-red/30"></div>
          </div>
        </div>
      )}

      {/* District Presidents */}
      {districtLeaders.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {t({ en: 'District Presidents', bn: 'জেলা সভাপতিবৃন্দ' })}
              </h2>
              <p className="text-gray-500 text-sm">
                {t({ en: 'Grassroots leaders driving change at the district level', bn: 'জেলা পর্যায়ে পরিবর্তনের চালিকাশক্তি তৃণমূল নেতারা' })}
              </p>
            </div>
            <div className={`grid gap-8 ${districtLeaders.length <= 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
              {districtLeaders.map((leader) => (
                <LeaderCard
                  key={leader._id}
                  leader={leader}
                  t={t}
                  onClick={() => navigate(`/leadership/${leader._id}`)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Central Leadership Separator */}
      {centralLeaders.length > 0 && (
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-party-green/30 to-party-green/30"></div>
            <div className="flex items-center gap-2 px-4 py-2 bg-party-green/5 rounded-full border border-party-green/20">
              <div className="w-2 h-2 bg-party-green rounded-full"></div>
              <span className="text-sm font-medium text-party-green tracking-wide">
                {t({ en: 'CENTRAL LEADERSHIP', bn: 'কেন্দ্রীয় নেতৃত্ব' })}
              </span>
              <div className="w-2 h-2 bg-party-green rounded-full"></div>
            </div>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-party-green/30 to-party-green/30"></div>
          </div>
        </div>
      )}

      {/* Central Leadership */}
      {centralLeaders.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {t({ en: 'Central Leadership', bn: 'কেন্দ্রীয় নেতৃত্ব' })}
              </h2>
              <p className="text-gray-500 text-sm">
                {t({ en: 'Key leaders driving the party\'s central operations and strategy', bn: 'দলের কেন্দ্রীয় কার্যক্রম ও কৌশল পরিচালনাকারী মূল নেতৃবৃন্দ' })}
              </p>
            </div>
            <div className={`grid gap-8 ${centralLeaders.length <= 3 ? 'md:grid-cols-3 max-w-4xl mx-auto' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
              {centralLeaders.map((leader) => (
                <LeaderCard
                  key={leader._id}
                  leader={leader}
                  t={t}
                  onClick={() => navigate(`/leadership/${leader._id}`)}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Leadership;
